import { Component } from "react";
import type { ErrorInfo, ReactNode } from "react";
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import { RefreshCw, Home, AlertCircle } from "lucide-react";
import { OctaLogo } from "@/components/octa-logo";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const RELOAD_KEY = "__chunk_reload_ts__";
const RELOAD_COUNT_KEY = "__chunk_reload_count__";
const MAX_RELOADS = 1;
const RELOAD_WINDOW = 60_000;

function isChunkLoadError(error: Error): boolean {
  const msg = (error.message || "").toLowerCase();
  return msg.includes("dynamically imported module") ||
    msg.includes("loading chunk") ||
    msg.includes("loading css chunk") ||
    msg.includes("unable to preload") ||
    msg.includes("is not a valid javascript mime type") ||
    msg.includes("importing a module script failed") ||
    msg.includes("failed to fetch") ||
    msg.includes("load failed");
}

function canReload(): boolean {
  try {
    const lastReload = Number(sessionStorage.getItem(RELOAD_KEY) || "0");
    const reloadCount = Number(sessionStorage.getItem(RELOAD_COUNT_KEY) || "0");
    const now = Date.now();
    if (now - lastReload > RELOAD_WINDOW) {
      sessionStorage.setItem(RELOAD_COUNT_KEY, "0");
      return true;
    }
    return reloadCount < MAX_RELOADS;
  } catch {
    return false;
  }
}

function markReload(): void {
  try {
    const count = Number(sessionStorage.getItem(RELOAD_COUNT_KEY) || "0");
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
    sessionStorage.setItem(RELOAD_COUNT_KEY, String(count + 1));
  } catch {}
}

function ChunkReloadFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background" data-testid="chunk-reload-fallback-manual">
      <div className="flex flex-col items-center gap-6 text-center px-4 max-w-md">
        <div className="flex items-center gap-2">
          <OctaLogo />
          <span className="text-sm font-semibold tracking-[0.15em] uppercase">OCTA</span>
          <span className="text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground">CORE</span>
        </div>
        <div className="flex flex-col gap-2">
          <h1 className="text-lg font-semibold" data-testid="text-chunk-error-title">A new version is available</h1>
          <p className="text-sm text-muted-foreground leading-relaxed" data-testid="text-chunk-error-description">
            The app has been updated and needs a fresh reload to continue working. This is not an error with your data or automations — just a routine update.
          </p>
        </div>
        <Button onClick={() => {
          try {
            sessionStorage.removeItem(RELOAD_KEY);
            sessionStorage.removeItem(RELOAD_COUNT_KEY);
          } catch {}
          window.location.reload();
        }} className="gap-2" data-testid="button-hard-refresh">
          <RefreshCw className="h-4 w-4" />
          Refresh now
        </Button>
      </div>
    </div>
  );
}

function ErrorFallback({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation("common");
  const [, setLocation] = useLocation();

  return (
    <div className="min-h-screen flex items-center justify-center bg-background" data-testid="error-boundary-fallback">
      <div className="flex flex-col items-center gap-6 text-center px-4 max-w-md">
        <div className="flex items-center gap-2">
          <OctaLogo />
          <span className="text-sm font-semibold tracking-[0.15em] uppercase">OCTA</span>
          <span className="text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground">CORE</span>
        </div>
        <div className="flex flex-col gap-2">
          <h1 className="text-lg font-semibold" data-testid="text-error-title">{t("error.title")}</h1>
          <p className="text-sm text-muted-foreground leading-relaxed" data-testid="text-error-description">
            {t("error.description")}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => setLocation("/")} className="gap-2" data-testid="button-go-home">
            <Home className="h-4 w-4" />
            {t("error.dashboard")}
          </Button>
          <Button variant="outline" onClick={onRetry} className="gap-2" data-testid="button-retry">
            <RefreshCw className="h-4 w-4" />
            Retry
          </Button>
          <Button onClick={() => window.location.reload()} className="gap-2" data-testid="button-try-again">
            <RefreshCw className="h-4 w-4" />
            {t("error.tryAgain")}
          </Button>
        </div>
      </div>
    </div>
  );
}

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: "full" | "widget";
}

interface ErrorBoundaryState {
  hasError: boolean;
  isChunkError: boolean;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, isChunkError: false };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    if (isChunkLoadError(error)) {
      return { hasError: true, isChunkError: true };
    }
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);

    const chunkError = isChunkLoadError(error);
    const loopDiag = (window as any).__DASH_LOOP_DIAG;

    Sentry.captureException(error, {
      contexts: {
        react: { componentStack: errorInfo.componentStack },
      },
      tags: {
        isChunkError: String(chunkError),
        ...(loopDiag ? { loopDiag } : {}),
      },
    });

    try {
      fetch("/api/client-error", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          message: loopDiag ? `${error.message} || DIAG: ${loopDiag}` : error.message,
          stack: error.stack?.slice(0, 2000),
          componentStack: errorInfo.componentStack?.slice(0, 2000),
          url: window.location.href,
          userAgent: navigator.userAgent,
          ...(chunkError ? { category: "chunk_reload" } : {}),
        }),
      }).catch((err) => { console.warn("[error-boundary] Failed to report error to server:", err); });
    } catch (err) { console.warn("[error-boundary] Error while preparing error report:", err); }

    if (chunkError && canReload()) {
      markReload();
      window.location.reload();
      return;
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, isChunkError: false });
  };

  render() {
    if (this.state.hasError) {
      if (this.state.isChunkError) {
        return <ChunkReloadFallback />;
      }
      if (this.props.fallback === "widget") {
        return <WidgetErrorFallback onRetry={this.handleRetry} />;
      }
      return <ErrorFallback onRetry={this.handleRetry} />;
    }

    return this.props.children;
  }
}

function WidgetErrorFallback({ onRetry }: { onRetry: () => void }) {
  return (
    <Card data-testid="widget-error-fallback">
      <CardContent className="p-5">
        <div className="flex flex-col items-center justify-center text-center gap-3 py-4">
          <div className="w-8 h-8 rounded-full bg-destructive/10 flex items-center justify-center">
            <AlertCircle className="h-4 w-4 text-destructive" />
          </div>
          <div className="space-y-1">
            <p className="text-sm font-medium" data-testid="text-widget-error-title">Something went wrong</p>
            <p className="text-xs text-muted-foreground">This widget encountered an error.</p>
          </div>
          <Button variant="outline" size="sm" onClick={onRetry} className="gap-1.5" data-testid="button-widget-retry">
            <RefreshCw className="h-3 w-3" />
            Retry
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
